<script setup lang="ts">
import { computed, toRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import { useTheme } from "@/composable/useTheme";
import { useGetDiagnostic } from "@/stores/queries";
import FieldObservationsList from "@/components/field-observations/FieldObservationsList.vue";
import FieldHeader from "@/components/field-observations/FieldHeader.vue";

const route = useRoute();
const router = useRouter();
const { t } = useTranslation();

useTheme("light");

const diagnosticId = computed(() => Number(route.params.diagnosticId));
const diagnosticIdRef = toRef(diagnosticId);

const { data: diagnostic, isLoading } = useGetDiagnostic(diagnosticIdRef);

const projectName = computed(() => diagnostic.value?.project?.name);

const back = () => {
  // If there's navigation history, go back
  if (window.history.length > 2) {
    router.back();
  } else {
    // Otherwise, navigate to the diagnostic page
    router.push({ name: "diagnostic", params: { id: diagnosticId.value } });
  }
};
</script>

<template>
  <div class="d-flex flex-column vh-100">
    <header
      class="field-obs-toolbar d-flex align-items-center fixed-top bg-blue-gradient"
      style="height: 60px; z-index: 2"
    >
      <FieldHeader class="w-100" @back="back">
        <template #title
          ><div class="d-flex flex-column">
            <small v-if="projectName" class="text-white-50">
              {{ projectName }}
            </small>
            <div class="fs-5">
              {{ diagnostic?.name || t("report:diagnostics.item.noname") }}
            </div>
          </div></template
        ></FieldHeader
      >
    </header>

    <main
      class="flex-grow-1 overflow-hidden d-flex flex-column"
      style="margin-top: 60px"
    >
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <FieldObservationsList
        v-else-if="diagnosticId"
        :diagnostic-id="diagnosticId"
      />
    </main>
  </div>
</template>

<style lang="scss">
@import "@/style/variables.scss";

.field-obs-toolbar {
  background-color: $dark !important;
  color: white;
}
</style>
